import Agent from "core/services/api";
import {
    UploadFile,
    Fragment,
    Result,
    UploadFileEntity,
    FragmentEntity,
    FragmentProperties,
    PublisherParams,
} from "core/models";

interface FragmentResultObject {
    fragments: FragmentEntity[];
    complete: boolean;
}

export interface FragmentProps {
    key: string;
    value: string | boolean;
}

interface FragmentsList {
    fragments: Fragment[];
    continuationToken?: string;
}

export type SelectionRequest = {
    trimStart: number;
    trimEnd: number;
    firstSegmentNumber: string;
    lastSegmentNumber: string;
    segmentCount: number;
};

type OrderFields = [string, boolean, string?, boolean?, string?, boolean?];

const createFragment = async (
    accessToken: string,
    projectID: number,
    channelID: number,
    title: string,
    description: string | undefined,
    selections: SelectionRequest[],
    thumbnailFileID?: number,
    tags?: string[],
    properties?: FragmentProperties[],
): Promise<Result<Fragment, string>> => {
    const request = {
        projectId: projectID,
        channelId: channelID,
        title,
        description,
        inGroupFragmentId: 0,
        selections,
        thumbnailFileId: thumbnailFileID,
        thumbnailTime: -1.0,
        tags,
        mediaFileIntroKey: null,
        mediaFileOutroKey: null,
        properties,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentResultObject>(
        "/fragment/createfromselections/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        console.error("result: ", result);
        return {
            value: undefined,
            error: "Unable to create fragment from selection",
        };
    }

    return {
        value: new Fragment(resultObject.fragments[0]),
        error: undefined,
    };
};
/**
 * Delete a given fragment
 * @param accessToken   Session access token
 * @param fragmentID    The fragment that should be deleted
 */
const deleteFragment = async (
    accessToken: string,
    fragmentID: number,
): Promise<Result<boolean, string>> => {
    const request = {
        id: fragmentID,
        byGroups: false,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<Record<string, unknown>>(
        "fragment/delete/v1",
        request,
        headers,
        "site",
    );
    return {
        value: result.resultObject !== undefined,
        error: undefined,
    };
};

/**
 * Lists fragments of a given project.
 * @param accessToken   Session access token
 * @param projectId     The project to list fragments from
 * @param searchText    String to search for
 * @param channelId     The Channel ID if filtered on
 * @param aspectRatio   The AspectRatio if filtered on
 * @param orderFields   Order of fragments in result, first value is what field to order on (string), second is asc / or desc (boolean)
 * @param maxCount      Max # of fragments in result
 *
 */

interface ListFragmentRequest {
    accessToken: string;
    projectId: number;
    searchText?: string;
    channelId?: number;
    aspectRatio?: string;
    userId?: number;
    orderFields?: OrderFields;
    maxCount?: number;
    continuationToken?: string;
}
const listFragments = async ({
    accessToken,
    projectId,
    searchText,
    channelId,
    aspectRatio,
    userId,
    orderFields = ["insertionDate", false],
    maxCount = 40,
    continuationToken,
}: ListFragmentRequest): Promise<Result<FragmentsList, string>> => {
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    let result;
    if (continuationToken) {
        const request = {
            continuationToken,
        };
        result = await Agent.post<FragmentResultObject[]>(
            "/fragment/listcontinued/v1",
            request,
            headers,
            "site",
        );
    } else {
        const request = {
            projectId,
            byGroups: false,
            orderFields,
            searchText,
            channelId,
            aspectRatio,
            userId,
            searchInTitle: true,
            searchInTags: true,
            searchSplitIntoWords: false,
            maxCount,
        };
        result = await Agent.post<FragmentResultObject[]>(
            "/fragment/list/v1",
            request,
            headers,
            "site",
        );
    }
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "Unable to list fragments",
        };
    }

    if (resultObject.length === 0) {
        return {
            value: { fragments: [] },
            error: undefined,
        };
    }

    let fragments: Fragment[] = [];
    for (let i = 0; i < resultObject.length; i += 1) {
        const list = resultObject[i].fragments.map((x) => new Fragment(x));
        fragments = fragments.concat(list);
    }

    return {
        value: { fragments, continuationToken: result.continuationToken },
        error: undefined,
    };
};

const createFragmentUploadFile = async (
    accessToken: string,
    projectID: number,
    extension = "png",
): Promise<Result<UploadFile, string>> => {
    const request = {
        projectId: projectID,
        extension,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UploadFileEntity>(
        "fragment/createuploadfile/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "Unable to start upload session",
        };
    }

    return {
        value: new UploadFile(result.resultObject),
        error: undefined,
    };
};

const createThumbnailUploadFile = async (
    accessToken: string,
    projectID: number,
): Promise<Result<UploadFile, string>> => {
    const request = {
        projectId: projectID,
        extension: ".jpg",
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UploadFileEntity>(
        "fragment/createthumbnailuploadfile/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "Unable to create thumbnail upload",
        };
    }

    return {
        value: new UploadFile(result.resultObject),
        error: undefined,
    };
};

export interface UpdateThumbnail {
    id: number;
    thumbnailTime?: number;
    thumbnailFileId?: number;
    ExcludeOtherFieldsIfProcessing?: boolean;
}
const updateThumbnail = async (
    accessToken: string,
    updateObject: UpdateThumbnail,
): Promise<Result<Fragment, string>> => {
    const request = {
        ...updateObject,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentEntity>(
        "fragment/updateThumbnail/v1",
        request,
        headers,
        "site",
    );
    if (!result.resultObject) {
        return {
            value: undefined,
            error: "Unable to update thumbnail",
        };
    }
    return {
        value: new Fragment(result.resultObject),
        error: undefined,
    };
};

export interface UpdateFragment {
    id: number;
    title: string;
    tags: string[];
    description?: string | undefined;
    properties?: FragmentProperties[];
}
const updateFragment = async (
    accessToken: string,
    changedProps: UpdateFragment,
): Promise<Result<Fragment, string>> => {
    const request = {
        id: changedProps.id,
        title: changedProps.title,
        tags: changedProps.tags,
        description: changedProps.description,
        properties: changedProps.properties,
        updateFieldNames: Object.keys(changedProps),
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentEntity>(
        "fragment/update/v1",
        request,
        headers,
        "site",
    );
    if (!result.resultObject) {
        return {
            value: undefined,
            error: "Unable to create thumbnail upload",
        };
    }
    return {
        value: new Fragment(result.resultObject),
        error: undefined,
    };
};

const createFragmentFromFile = async (
    accessToken: string,
    projectID: number,
    title: string,
    fileID: number,
    tags?: string[],
    introKey?: string,
    outroKey?: string,
): Promise<Result<Fragment, string>> => {
    const request = {
        projectId: projectID,
        title,
        tags,
        properties: [],
        inGroupFragmentId: 0,
        fileId: fileID,
        mediaFileIntroKey: introKey,
        mediaFileOutroKey: outroKey,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentResultObject>(
        "fragment/createfromfile/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "unable to create fragment",
        };
    }

    return {
        value: new Fragment(result.resultObject.fragments[0]),
        error: undefined,
    };
};

const getFragment = async (
    accessToken: string,
    fragmentID: number,
): Promise<Result<Fragment, string>> => {
    const request = {
        id: fragmentID,
        byGroups: false,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentResultObject>(
        "fragment/get/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "unable to fetch fragment",
        };
    }

    return {
        value: new Fragment(resultObject.fragments[0]),
        error: undefined,
    };
};

const getMultipleFragments = async (
    accessToken: string,
    fragmentArr: number[],
): Promise<Result<Fragment[], string>> => {
    const request = {
        ids: fragmentArr,
        byGroups: false,
        excludeOtherFieldsIfProcessing: true,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentResultObject[]>(
        "fragment/getmultiple/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "unable to fetch fragments",
        };
    }

    if (resultObject.length === 0) {
        return {
            value: [],
            error: undefined,
        };
    }

    let fragments: Fragment[] = [];
    for (let i = 0; i < resultObject.length; i += 1) {
        const list = resultObject[i].fragments.map((x) => new Fragment(x));
        fragments = fragments.concat(list);
    }

    return {
        value: fragments,
        error: undefined,
    };
};

const updateVariantsFromFormatKey = async (
    accessToken: string,
    formatKey: string,
    fragmentID: number,
): Promise<Result<string, string>> => {
    const request = {
        id: fragmentID,
        formatKey,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    await Agent.post<Record<string, unknown>>(
        "fragment/updatevariantsfromformatkey/v1",
        request,
        headers,
        "site",
    );
    return {
        value: "",
        error: undefined,
    };
};

const createCompilation = async (
    accessToken: string,
    sourceFragmentIds: number[],
): Promise<Result<Fragment, string>> => {
    const request = {
        sourceFragmentIds,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentResultObject>(
        "fragment/createcompilation/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;

    if (!resultObject) {
        return {
            value: undefined,
            error: "unable to create compilation",
        };
    }
    return {
        value: new Fragment(resultObject.fragments[0]),
        error: undefined,
    };
};

export type CropSetting = {
    start: number;
    cropLeft: number;
    cropTop: number;
    cropWidth: number;
    cropHeight: number;
};

export type EditRequest = {
    sourceFragmentId: number;
    thumbnailTime?: number;
    thumbnailFileId?: number;
    trimStart?: number;
    trimEnd?: number;
    targetWidth?: number;
    targetHeight?: number;
    usePadding?: boolean;
    paddingBlurred?: boolean;
    cropSettings?: CropSetting[];
    mediaFileOverlayKey?: string | null;
    mediaFileFontKey?: string | null;
    fontSize?: number;
    fontColor?: string | null;
    fontLeft?: number;
    fontTop?: number;
    overlayText?: string | null;
    mediaFileIntroKey?: string | null;
    mediaFileOutroKey?: string | null;
    updateSource?: boolean;
};

const createEdit = async (
    accessToken: string,
    editRequest: EditRequest = {
        sourceFragmentId: 0,
        thumbnailTime: -1.0,
        thumbnailFileId: 0,
        trimStart: 0.0,
        trimEnd: 0.0,
        targetWidth: 100,
        targetHeight: 100,
        usePadding: false,
        paddingBlurred: false,
        cropSettings: [
            {
                start: 0.0,
                cropLeft: 0,
                cropTop: 0,
                cropWidth: 100,
                cropHeight: 100,
            },
        ],
        mediaFileOverlayKey: null,
        mediaFileFontKey: null,
        fontSize: 0,
        fontColor: null,
        fontLeft: 0,
        fontTop: 0,
        overlayText: null,
        mediaFileIntroKey: null,
        mediaFileOutroKey: null,
        updateSource: false,
    },
): Promise<Result<Fragment, string>> => {
    const request = editRequest;
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentResultObject>(
        "fragment/createedit/v1",
        request,
        headers,
        "site",
    );
    const { resultObject } = result;
    if (!resultObject) {
        return {
            value: undefined,
            error: "unable to create fragment edit",
        };
    }
    return {
        value: new Fragment(resultObject.fragments[0]),
        error: undefined,
    };
};

const publishFragment = async (
    accessToken: string,
    fragmentId: number,
    publisherKey: string,
    formatKey?: string,
    metaVariantLanguage?: string,
    publisherParams?: PublisherParams,
): Promise<Result<Fragment, string>> => {
    const request = {
        id: fragmentId,
        publishSettingKey: publisherKey,
        formatKey: formatKey || "mp4",
        metaVariantType: metaVariantLanguage ? "Subtitles" : null,
        metaVariantLanguage: metaVariantLanguage || null,
        excludeOtherFieldsIfProcessing: true,
        operation: null,
        parameters: publisherParams ? JSON.stringify(publisherParams) : null,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentEntity>(
        `fragment/publish/v1`,
        request,
        headers,
        "site",
    );
    if (!result.resultObject) {
        return {
            value: undefined,
            error: "Unable to create thumbnail upload",
        };
    }
    return {
        value: new Fragment(result.resultObject),
        error: undefined,
    };
};

const preparePublish = async <T>(
    accessToken: string,
    fragmentID: number,
    publisherKey: string,
    operation: string,
): Promise<Result<T, string>> => {
    const request = {};
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<string>(
        `fragment/preparepublish/v1/${fragmentID}/${publisherKey}/${operation}`,
        request,
        headers,
        "site",
    );
    if (!result.resultObject) {
        return {
            value: undefined,
            error: "Unable to create thumbnail upload",
        };
    }
    return {
        value: await JSON.parse(result.resultObject),
        error: undefined,
    };
};

const createVariantUploadFile = async (
    accessToken: string,
    projectID: number,
    extension: string,
): Promise<Result<UploadFile, string>> => {
    const request = {
        projectId: projectID,
        extension,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UploadFileEntity>(
        "fragment/createvariantuploadfile/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "unable to create fragment",
        };
    }

    return {
        value: new UploadFile(result.resultObject),
        error: undefined,
    };
};

const updateMetaVariantsFromFile = async (
    accessToken: string,
    fragmentId: number,
    fileId: number,
    language: string,
): Promise<Result<Fragment, string>> => {
    const request = {
        id: fragmentId,
        fileId,
        type: "Subtitles",
        language,
        excludeOtherFieldsIfProcessing: false,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<FragmentEntity>(
        "fragment/updatemetavariantsfromfile/v1",
        request,
        headers,
        "site",
    );
    if (!result.resultObject) {
        return {
            value: undefined,
            error: "Unable to create variants upload",
        };
    }
    return {
        value: new Fragment(result.resultObject),
        error: undefined,
    };
};

export {
    listFragments,
    getFragment,
    createFragment,
    deleteFragment,
    createFragmentUploadFile,
    createFragmentFromFile,
    updateVariantsFromFormatKey,
    createThumbnailUploadFile,
    createEdit,
    getMultipleFragments,
    createCompilation,
    updateFragment,
    updateThumbnail,
    publishFragment,
    preparePublish,
    createVariantUploadFile,
    updateMetaVariantsFromFile,
};
